import { api } from "./client";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

// Payment System v3.0 PR 5 — Payment Instruction Report API client.

export type PirFormat = "csv" | "pdf";
export type PirScope = "AllLive" | "Loan" | "Pool";

export interface PirListItem {
  id: string;
  reportNumber: string;
  loanId: string | null;
  poolId: string | null;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
  generatedAt: string;
  generatedByUserId: string;
  format: PirFormat | string;
  totalDisbursements: number;
  totalAmount: number;
}

export interface PirDisbursement {
  disbursementId: string;
  paymentId: string;
  date: string;
  recipientEntityId: string | null;
  recipientEntity: string;
  recipientAccount: string;
  recipientBank: string;
  recipientAba: string;
  recipientAccountNumber: string;
  amount: number;
  memo: string;
  isInstructed: boolean;
}

export interface PirGroup {
  sourceAccountKey: string;
  sourceAccountDisplay: string;
  disbursements: PirDisbursement[];
}

export interface PirDetail extends PirListItem {
  groups: PirGroup[];
}

export interface PirGenerateRequest {
  scope: PirScope;
  rangeStart?: string | null;
  rangeEnd?: string | null;
  format: PirFormat;
  loanId?: string | null;
  poolId?: string | null;
}

export const pirsApi = {
  list: () => api.get<PirListItem[]>("/pirs"),
  get: (id: string) => api.get<PirDetail>(`/pirs/${id}`),
  generate: (req: PirGenerateRequest) =>
    api.post<PirListItem>("/pirs/generate", req),
  markInstructed: (pirId: string, disbursementId: string) =>
    api.post<null>(`/pirs/${pirId}/mark-instructed/${disbursementId}`, undefined),
  downloadCsv: async (id: string): Promise<Blob> => {
    const resp = await fetch(`${API_BASE}/pirs/${id}/csv`, { credentials: "include" });
    if (!resp.ok) throw new Error(`Download CSV failed (${resp.status})`);
    return resp.blob();
  },
  downloadPdf: async (id: string): Promise<Blob> => {
    const resp = await fetch(`${API_BASE}/pirs/${id}/pdf`, { credentials: "include" });
    if (!resp.ok) throw new Error(`Download PDF failed (${resp.status})`);
    return resp.blob();
  },
};

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
